import { useParams, useNavigate } from 'react-router-dom';
import { useLocale, formatCurrency } from '@platform/core';
import { useBudget } from '../contexts/BudgetContext';

export const BudgetDetailPage: React.FC = () => {
  const { budgetId } = useParams<{ budgetId: string }>();
  const navigate = useNavigate();
  const { t } = useLocale();
  const { budgets, entries, currentBudget, selectBudget, createEntry, updateEntry, deleteEntry } = useBudget();

  // Find budget by ID
  const budget = budgets.find(b => b.id === budgetId);

  if (!budget) {
    return (
      <div className="not-found">
        <h2>{t('errors.notFound')}</h2>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          {t('nav.backToDashboard')}
        </button>
      </div>
    );
  }

  // Select this budget if not current
  if (currentBudget?.id !== budget.id) {
    selectBudget(budget.id);
  }

  const budgetEntries = entries.filter(e => e.budgetId === budget.id);

  const handleAddEntry = () => {
    const subject = budget.subjects[0];
    if (!subject) return;

    createEntry({
      budgetId: budget.id,
      subjectId: subject.id,
      period: new Date().toISOString().slice(0, 7),
      amount: 0,
      measure: 'actual',
    });
  };

  return (
    <div className="budget-detail-page">
      <div className="page-header">
        <button className="btn" onClick={() => navigate('/')}>
          ← {t('nav.back')}
        </button>
        <h1>{budget.name}</h1>
        <span className="badge">{budget.intervalType}</span>
      </div>

      {budget.description && (
        <p className="budget-description">{budget.description}</p>
      )}

      <div className="entries-section">
        <div className="section-header">
          <h2>{t('budget.entries')}</h2>
          <button className="btn btn-primary" onClick={handleAddEntry}>
            + {t('budget.addEntry')}
          </button>
        </div>

        {budgetEntries.length === 0 ? (
          <div className="empty-state">
            <p>{t('budget.noEntries')}</p>
          </div>
        ) : (
          <table className="entries-table">
            <thead>
              <tr>
                <th>{t('budget.period')}</th>
                <th>{t('budget.subject')}</th>
                <th>{t('budget.measure')}</th>
                <th className="text-end">{t('budget.amount')}</th>
                <th>{t('budget.notes')}</th>
                <th>{t('actions.title')}</th>
              </tr>
            </thead>
            <tbody>
              {budgetEntries.map((entry) => {
                const subject = budget.subjects.find(s => s.id === entry.subjectId);
                return (
                  <tr key={entry.id}>
                    <td>{entry.period}</td>
                    <td>{subject?.name || '-'}</td>
                    <td>
                      <span className={`badge badge-${entry.measure}`}>
                        {entry.measure}
                      </span>
                    </td>
                    <td className="text-end amount">{formatCurrency(entry.amount)}</td>
                    <td className="text-muted">{entry.notes || '-'}</td>
                    <td>
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          const amount = prompt(t('budget.enterAmount'), entry.amount.toString());
                          if (amount !== null) {
                            updateEntry(entry.id, { amount: parseFloat(amount) || 0 });
                          }
                        }}
                      >
                        {t('actions.edit')}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          if (confirm(t('actions.confirmDelete'))) {
                            deleteEntry(entry.id);
                          }
                        }}
                      >
                        {t('actions.delete')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <style>{`
        .budget-detail-page {
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .page-header h1 {
          flex: 1;
          margin: 0;
        }

        .badge {
          padding: 0.25rem 0.75rem;
          border-radius: var(--radius);
          font-size: 0.75rem;
          background: var(--color-background);
        }

        .badge-budget { background: #dbeafe; color: #1e40af; }
        .badge-actual { background: #dcfce7; color: #166534; }
        .badge-goal { background: #fef3c7; color: #92400e; }

        .budget-description {
          color: var(--color-text-secondary);
          margin-bottom: 1.5rem;
        }

        .entries-section {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow);
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        .section-header h2 {
          margin: 0;
          font-size: 1.125rem;
        }

        .entries-table {
          width: 100%;
        }

        .text-end { text-align: end; }

        .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          margin-inline-end: 0.25rem;
        }

        .empty-state {
          text-align: center;
          padding: 2rem;
          color: var(--color-text-secondary);
        }

        .not-found {
          text-align: center;
          padding: 3rem;
        }
      `}</style>
    </div>
  );
};
